import { useProjectParams } from '@hooks/useProjectParams';
import { ButtonWithTooltip } from 'components/Form/ButtonWithTooltip';
import { Toolbar } from 'components/Toolbar';
import type { ProjectData } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useHandleCreateAgreements, useHandleEditContract, useHandleUploadContract } from 'hooks/useHandlersContract';
import { Button, cnMixSpace } from 'uikit';

import { CreateWorkOrder } from '../CreateWorkOrder';
import { StoringHistoryChange } from '../StoringHistoryChange';

import Style from './ContractToolbar.module.css';

export const ContractToolbar = ({
    projectId,
    contractData,
    statusContract,
    projectData,
}: {
    projectId?: string;
    contractData?: Record<string, any>;
    statusContract?: ContractStatusEnum;
    projectData: ProjectData | undefined | null;
}) => {
    const { methodology } = useProjectParams();
    const { contract: contractRules, additionalAgreement: additionalAgreementRules } = useAccess();
    const handleUploadClick = useHandleUploadContract(projectId, methodology);
    const handleEditClick = useHandleEditContract(projectId, methodology);
    const handleCreateAgreementsClick = useHandleCreateAgreements(projectId, methodology, contractData?.id);
    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;
    const isContractStatusCompleted = statusContract === ContractStatusEnum.Completed;
    const isCreateAgreementBtnDisabled = !!contractData?.unreadyUerRowCount && !contractData?.isPriceCollectionLoaded;
    const isAvailableWorkOrder = !!projectData?.contractNavigation?.data?.workOrderNavs?.length;

    const isProjectStatusDelayed = projectData?.status === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = projectData?.status === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isDisabledButtonUpload =
        !contractRules.canCreate || !isContractStatusWork || isDisabledByProjectStatus || isAvailableWorkOrder;

    const isDisabledCreateDC =
        isCreateAgreementBtnDisabled ||
        isDisabledByProjectStatus ||
        !isContractStatusWork ||
        !additionalAgreementRules.canCreate;

    return (
        <Toolbar flex fullLine>
            <ButtonWithTooltip
                label="Загрузить новый файл"
                view="secondary"
                size="s"
                onClick={handleUploadClick}
                className={cnMixSpace({ mR: 's' })}
                disabled={isDisabledButtonUpload}
                tooltipText="По договору имеются Наряд-Заказы, загрузка нового файла недоступна"
                direction="upCenter"
                isTooltipHidden={!isAvailableWorkOrder}
            />
            <Button
                label="Редактировать"
                view="secondary"
                size="s"
                onClick={handleEditClick}
                className={cnMixSpace({ mR: 's' })}
                disabled={!contractRules.canEdit || isDisabledByProjectStatus || isContractStatusCompleted}
            />
            <ButtonWithTooltip
                id="rim_create DS button_pressed"
                label="Создать ДС"
                view="secondary"
                size="s"
                onClick={handleCreateAgreementsClick}
                className={cnMixSpace({ mR: 's' })}
                disabled={isDisabledCreateDC}
                direction="upCenter"
                tooltipText="Отсутствует предмет ДС"
                isTooltipHidden={!isDisabledCreateDC}
                classNameTooltip={Style.classNameTooltipWidth}
            />
            <CreateWorkOrder
                projectId={projectId}
                contractData={contractData}
                statusContract={statusContract}
                statusProject={projectData?.status}
            />
            <StoringHistoryChange contractData={contractData} />
        </Toolbar>
    );
};

ContractToolbar.displayName = 'ContractToolbar';
